import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  faCheckCircle,
  faHandsHelping,
  faMapMarkerAlt,
  faPhone,
  faMoneyBillWave,
  faCalendarDays,
  faClock,
  faUser,
  faArrowLeft,
} from "@fortawesome/free-solid-svg-icons";

const HerKartServiceBookingSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { booking } = location.state || {};

  if (!booking) {
    navigate("/herkart/services", { replace: true });
    return null;
  }

  const {
    customer,
    serviceName,
    price,
    duration,
    preferredDate,
    preferredSlot,
    paymentMode,
    bookedAt,
    bookingNo,
  } = booking;

  const bookedDate = new Date(bookedAt).toLocaleString();
  const wantedDate = new Date(preferredDate).toLocaleDateString();

  return (
    <div className="min-h-screen bg-[#050816] text-white">
      <div className="sticky top-0 z-40 border-b border-white/10 bg-[#050816]/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-4">
          <Link
            to="/herkart/services"
            className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 hover:bg-white/20"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </Link>

          <div>
            <h1 className="text-xl font-bold">Booking Confirmed</h1>
            <p className="text-xs text-slate-200">FoundHer Marketplace</p>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-4 py-8">
        <div className="rounded-3xl border border-emerald-500/20 bg-emerald-500/10 p-8 text-center">
          <FontAwesomeIcon icon={faCheckCircle} className="mx-auto text-7xl text-emerald-400" />

          <h2 className="mt-6 text-3xl text-cyan-200 font-bold">Your Service Has Been Booked!</h2>

          <p className="mx-auto mt-3 max-w-2xl text-slate-100">
            Thank you for supporting women entrepreneurs through FoundHer. The
            provider will confirm your booking shortly.
          </p>

          {bookingNo && (
            <p className="mt-4 text-sm text-slate-300">
              Booking No: <span className="font-semibold text-pink-300">{bookingNo}</span>
            </p>
          )}
        </div>

        <div className="mt-8 rounded-3xl border border-white/10 bg-white/5 p-6">
          <h3 className="mb-5 text-xl text-cyan-100 font-bold flex items-center gap-2">
            <FontAwesomeIcon icon={faHandsHelping} className="text-pink-400" />
            {serviceName}
          </h3>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="flex items-start gap-3">
              <FontAwesomeIcon icon={faUser} className="mt-1 text-pink-400" />
              <div>
                <p className="text-xs text-slate-200">Customer</p>
                <p className="text-gray-100 font-semibold">{customer?.name}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <FontAwesomeIcon icon={faPhone} className="mt-1 text-pink-400" />
              <div>
                <p className="text-xs text-slate-200">Mobile</p>
                <p className="font-semibold text-slate-100">{customer?.mobile}</p>
              </div>
            </div>

            <div className="md:col-span-2 flex items-start gap-3">
              <FontAwesomeIcon icon={faMapMarkerAlt} className="mt-1 text-pink-400" />
              <div>
                <p className="text-xs text-slate-200">Service Address</p>
                <p className="text-gray-100 font-semibold leading-7">
                  {customer?.house}, {customer?.street}
                  {customer?.landmark ? `, ${customer.landmark}` : ""}
                  <br />
                  {customer?.city}, {customer?.state} - {customer?.pincode}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <FontAwesomeIcon icon={faCalendarDays} className="mt-1 text-pink-400" />
              <div>
                <p className="text-xs text-slate-200">Preferred Date</p>
                <p className="font-semibold text-slate-100">{wantedDate}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <FontAwesomeIcon icon={faClock} className="mt-1 text-pink-400" />
              <div>
                <p className="text-xs text-slate-200">Preferred Slot</p>
                <p className="font-semibold text-slate-100">{preferredSlot} {duration ? `(${duration})` : ""}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 grid gap-6 md:grid-cols-2">
          <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/10 p-4">
            <div className="flex items-start gap-3">
              <FontAwesomeIcon icon={faMoneyBillWave} className="mt-1 text-emerald-400" />
              <div>
                <h4 className="font-semibold text-cyan-50">Payment Mode</h4>
                <p className="text-sm text-slate-100">{paymentMode}</p>
                <p className="mt-1 text-lg font-bold text-pink-400">₹{price}</p>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
            <div className="flex items-start gap-3">
              <FontAwesomeIcon icon={faCalendarDays} className="mt-1 text-pink-400" />
              <div>
                <h4 className="font-semibold text-cyan-50">Booked On</h4>
                <p className="text-sm text-slate-100">{bookedDate}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 space-y-3">
          {bookingNo && (
            <Link
              to={`/herkart/service-bookings/${bookingNo}`}
              className="flex w-full items-center justify-center gap-3 rounded-2xl border border-pink-500/40 bg-pink-500/10 py-4 font-semibold text-pink-300 transition hover:bg-pink-500/20"
            >
              View Booking Details
            </Link>
          )}

          <Link
            to="/herkart/services"
            className="flex w-full items-center justify-center gap-3 rounded-2xl bg-pink-600 py-4 font-semibold transition hover:bg-pink-500"
          >
            <FontAwesomeIcon icon={faHandsHelping} />
            Browse More Services
          </Link>
        </div>
      </div>
    </div>
  );
};

export default HerKartServiceBookingSuccess;
